import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type SessionDetail } from "../lib/api.js";
import { czyOdpytywac } from "../lib/sessionStatus.js";

/** Co ile odswiezamy sesje, dopoki cos sie liczy. */
export const INTERWAL_ODPYTYWANIA_MS = 3000;

export interface WynikSesji {
  session: SessionDetail | null;
  /** Tylko 404 znaczy, ze sesji naprawde nie ma. */
  missing: boolean;
  /** Przejsciowy blad sieci: dane zostaja, ekran pokazuje baner stanu (BN-1). */
  glitch: boolean;
  /**
   * Pobiera sesje natychmiast. To jest sposob na "odswiez po akcji": odpytywanie samo zatrzymuje sie
   * na DONE i na SPLIT_REVIEW, wiec po "Zatwierdz i oceń", "Oceń ponownie" albo zmianie nazwy mowcy
   * ekran musi sam poprosic o nowy stan. Po zmianie statusu na taki, ktory sie liczy (np. LENSES),
   * odpytywanie rusza samo.
   */
  odswiez: () => Promise<void>;
}

/**
 * Sesja z odpytywaniem (S-7: dane podmieniaja sie po cichu, zero migotania spinnerem).
 *
 * Odpytywanie trwa, dopoki sesja sie liczy (patrz czyOdpytywac) albo dopoki jej jeszcze nie ma. Trwa tez po chwilowym
 * bledzie sieci (`glitch`), nawet gdy sesja jest juz skonczona (DONE): baner "ponawiam probe" nie moze obiecywac
 * ponowienia, ktorego nikt nie robi (np. po zapisie wyboru mowcy, gdy odswiezenie sesji padlo).
 *
 * Odpowiedzi nadchodzace w zlej kolejnosci nie cofaja ekranu: odpowiedz starsza niz juz zastosowana
 * jest pomijana. Zastosowana bywa kazda nowsza od poprzedniej, a nie tylko odpowiedz na ostatnie
 * zapytanie - inaczej wolny serwer (odpowiedz dluzsza niz interwal) nie odswiezylby ekranu nigdy,
 * bo kazde nowe zapytanie uniewazniaaloby poprzednie.
 */
export function useSesja(id: string | undefined): WynikSesji {
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [glitch, setGlitch] = useState(false);
  const numerZapytania = useRef(0);
  /** Numer najnowszego zapytania, ktorego wynik trafil na ekran. */
  const zastosowany = useRef(0);
  const zywy = useRef(true);
  /** Id, ktorego sesje ogladamy teraz: odpowiedz na zapytanie o poprzednie id nie moze trafic na ekran. */
  const biezaceId = useRef(id);
  biezaceId.current = id;

  useEffect(() => {
    zywy.current = true;
    return () => {
      zywy.current = false;
    };
  }, []);

  const odswiez = useCallback(async (): Promise<void> => {
    if (!id) return;
    const moje = ++numerZapytania.current;
    try {
      const dane = await api.session(id);
      if (!zywy.current || biezaceId.current !== id || moje < zastosowany.current) return;
      zastosowany.current = moje;
      setSession(dane);
      setMissing(false);
      setGlitch(false);
    } catch (err) {
      if (!zywy.current || biezaceId.current !== id || moje < zastosowany.current) return;
      zastosowany.current = moje;
      // 401, 500, 502 i brak sieci sa przejsciowe i ponowienie ma sens.
      if (err instanceof ApiError && err.status === 404) {
        setMissing(true);
        return;
      }
      setGlitch(true);
    }
  }, [id]);

  useEffect(() => {
    setSession(null);
    setMissing(false);
    setGlitch(false);
    void odswiez();
  }, [odswiez]);

  const status = session?.status ?? null;
  const odpytuj = status === null || czyOdpytywac(status) || glitch;
  useEffect(() => {
    if (!odpytuj || missing) return;
    const t = setInterval(() => void odswiez(), INTERWAL_ODPYTYWANIA_MS);
    return () => clearInterval(t);
  }, [odpytuj, missing, odswiez]);

  return { session, missing, glitch, odswiez };
}
